import React, { useEffect, useState } from "react";
import {
  FilePenLine,
  Trash2,
  Copy,
  Eye
} from "lucide-react";
import axios from "axios";
import { useTutorAuth } from "../../context/tutorAuthContext";
import GroupClassStudentsTab from "../../components/tutor/GroupClassStudentsTab";
import {
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button
} from "@mui/material";

const BASE_URL = process.env.REACT_APP_BASE_URL;

const CreateCourseDashboard = () => {
  const { tutor } = useTutorAuth();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [courses, setCourses] = useState([]);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success"
  });
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [classToDelete, setClassToDelete] = useState(null);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("classes");
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [curriculumItems, setCurriculumItems] = useState([]);
  const [curriculumSummary, setCurriculumSummary] = useState({ totalItems: 0, completedItems: 0, progress: 0 });
  const [curriculumLoading, setCurriculumLoading] = useState(false);
  const [newCurriculumItem, setNewCurriculumItem] = useState("");
  const [editData, setEditData] = useState({
    title: "",
    maxStudents: 1,
    price: "",
    description: "",
    videoLink: "",
    classLink: "",
    certificateType: "none",
    curriculum: "",
    endDate: ""
  });

  const [courseData, setCourseData] = useState({
    title: "",
    maxStudents: 1,
    price: "",
    description: "",
    videoLink: "",
    classLink: "",
    certificateType: "none",
    curriculum: null,
    startDate: "",
    endDate: ""
  });

  const wordCount = courseData.description.trim()
    ? courseData.description.trim().split(/\s+/).length
    : 0;

  /* -------------------------
     FETCH CLASSES
  -------------------------- */
  useEffect(() => {
    if (!tutor?.id) return;

    fetchCourses();
  }, [tutor]);

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const handleSnackbarClose = (_, reason) => {
    if (reason === "clickaway") return;
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const fetchCourses = async () => {
    try {
      const res = await axios.get(
        `${BASE_URL}/api/tutors/group-classes/${tutor.id}`
      );
      setCourses(res.data.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchCurriculumItems = async (classId) => {
    if (!classId || !tutor?.id) return;
    setCurriculumLoading(true);
    try {
      const res = await axios.get(
        `${BASE_URL}/api/tutors/group-classes/${classId}/curriculum/${tutor.id}`
      );
      const payload = res.data?.data || {};
      setCurriculumItems(payload.items || []);
      setCurriculumSummary(payload.summary || { totalItems: 0, completedItems: 0, progress: 0 });
    } catch (err) {
      console.error(err);
      showSnackbar("Failed to load curriculum items", "error");
    } finally {
      setCurriculumLoading(false);
    }
  };

  /* -------------------------
     HANDLE INPUT
  -------------------------- */
  const handleChange = (e) => {
    const { name, value, files } = e.target;

    if (files) {
      setCourseData((prev) => ({ ...prev, [name]: files[0] }));
    } else {
      setCourseData((prev) => ({ ...prev, [name]: value }));
    }
  };

  /* -------------------------
     COPY COUPON
  -------------------------- */
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    showSnackbar(`Coupon ${text} copied`, "info");
  };

  const copyShareLink = (classId) => {
    const url = `${window.location.origin}/group-classes/${classId}`;
    navigator.clipboard.writeText(url);
    showSnackbar("Share URL copied", "info");
  };

  /* -------------------------
     FORMAT DATE
  -------------------------- */
  const formatDate = (date) => {
    if (!date) return "-";
    return new Date(date).toLocaleString();
  };

  const toDateTimeLocal = (date) => {
    if (!date) return "";
    const d = new Date(date);
    const tzOffset = d.getTimezoneOffset() * 60000;
    return new Date(d.getTime() - tzOffset).toISOString().slice(0, 16);
  };

  /* -------------------------
     CLASS STATUS
  -------------------------- */
  const getStatus = (course) => {
    const now = new Date();
    const start = new Date(course.start_date);
    const end = new Date(course.end_date);

    if (now < start) return "Not Started";
    if (now > end) return "Closed";
    return "Ongoing";
  };

  /* -------------------------
     CREATE CLASS
  -------------------------- */
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!tutor?.id) {
      showSnackbar("Tutor not logged in", "error");
      return;
    }

    if (wordCount < 50) {
      showSnackbar("Description must be at least 50 words", "error");
      return;
    }

    if (!courseData.startDate || !courseData.endDate) {
      showSnackbar("Start and End date required", "error");
      return;
    }

    if (new Date(courseData.endDate) <= new Date(courseData.startDate)) {
      showSnackbar("End date must be after start date", "error");
      return;
    }

    setIsSubmitting(true);

    try {
      const res = await axios.post(
        `${BASE_URL}/api/tutors/create-group-class`,
        {
          tutorId: tutor.id,
          title: courseData.title,
          description: courseData.description,
          videoLink: courseData.videoLink,
          classLink: courseData.classLink,
          maxStudents: courseData.maxStudents,
          price: courseData.price,
          curriculum: courseData.curriculum,
          certificateType: courseData.certificateType,
          startDate: courseData.startDate,
          endDate: courseData.endDate
        }
      );

      showSnackbar(`Created! Coupon: ${res.data.couponCode}`, "success");

      await fetchCourses();
      closeCreateModal();

      // RESET FORM
      setCourseData({
        title: "",
        maxStudents: 1,
        price: "",
        description: "",
        videoLink: "",
        classLink: "",
        certificateType: "none",
        curriculum: null,
        startDate: "",
        endDate: ""
      });

    } catch (err) {
      console.error(err);
      showSnackbar("Failed to create class", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  /* -------------------------
     DELETE CLASS
  -------------------------- */
  const openDeleteModal = (course) => {
    setClassToDelete(course);
    setDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    setDeleteModalOpen(false);
    setClassToDelete(null);
  };

  const handleDelete = async () => {
    if (!classToDelete?.id) return;

    try {
      await axios.delete(
        `${BASE_URL}/api/tutors/delete-group-class/${classToDelete.id}`
      );

      setCourses((prev) => prev.filter((c) => c.id !== classToDelete.id));
      showSnackbar("Class deleted successfully", "success");
      closeDeleteModal();
    } catch (err) {
      console.error(err);
      showSnackbar("Delete failed", "error");
    }
  };

  const openCreateModal = () => {
    setCreateModalOpen(true);
  };

  const closeCreateModal = () => {
    setCreateModalOpen(false);
  };

  const openViewModal = (course) => {
    setSelectedCourse(course);
    setViewModalOpen(true);
    fetchCurriculumItems(course.id);
  };

  const closeViewModal = () => {
    setViewModalOpen(false);
    setSelectedCourse(null);
  };

  const openEditModal = (course) => {
    setSelectedCourse(course);
    setEditData({
      title: course.title || "",
      maxStudents: course.max_students || 1,
      price: course.price || "",
      description: course.description || "",
      videoLink: course.video_link || "",
      classLink: course.class_link || "",
      certificateType: course.certificate_type || "none",
      curriculum: course.curriculum || "",
      endDate: toDateTimeLocal(course.end_date)
    });
    setEditModalOpen(true);
  };

  const closeEditModal = () => {
    setEditModalOpen(false);
    setSelectedCourse(null);
    setEditData({
      title: "",
      maxStudents: 1,
      price: "",
      description: "",
      videoLink: "",
      classLink: "",
      certificateType: "none",
      curriculum: "",
      endDate: ""
    });
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditData((prev) => ({ ...prev, [name]: value }));
  };

  const handleUpdate = async (e) => {
    e.preventDefault();

    if (!selectedCourse?.id) return;

    if (!editData.title || !editData.price || !editData.classLink || !editData.endDate) {
      showSnackbar("Title, price, class link and end date are required", "error");
      return;
    }

    setIsUpdating(true);

    try {
      await axios.put(
        `${BASE_URL}/api/tutors/update-group-class/${selectedCourse.id}`,
        {
          title: editData.title,
          description: editData.description,
          videoLink: editData.videoLink,
          classLink: editData.classLink,
          maxStudents: Number(editData.maxStudents),
          price: Number(editData.price),
          curriculum: editData.curriculum,
          certificateType: editData.certificateType,
          endDate: editData.endDate
        }
      );

      showSnackbar("Class updated successfully", "success");
      await fetchCourses();
      closeEditModal();
    } catch (err) {
      console.error(err);
      showSnackbar("Failed to update class", "error");
    } finally {
      setIsUpdating(false);
    }
  };

  const addCurriculumItem = async () => {
    if (!selectedCourse?.id || !newCurriculumItem.trim()) return;
    try {
      await axios.post(
        `${BASE_URL}/api/tutors/group-classes/${selectedCourse.id}/curriculum/${tutor.id}`,
        { title: newCurriculumItem.trim() }
      );
      setNewCurriculumItem("");
      await fetchCurriculumItems(selectedCourse.id);
      showSnackbar("Curriculum item added", "success");
    } catch (err) {
      console.error(err);
      showSnackbar("Failed to add curriculum item", "error");
    }
  };

  const toggleCurriculumItem = async (item) => {
    if (!selectedCourse?.id) return;
    try {
      await axios.put(
        `${BASE_URL}/api/tutors/group-classes/${selectedCourse.id}/curriculum/${item.id}/${tutor.id}`,
        {
          title: item.title,
          details: item.details,
          sort_order: item.sort_order,
          is_completed: Number(item.is_completed) === 1 ? 0 : 1,
        }
      );
      await fetchCurriculumItems(selectedCourse.id);
    } catch (err) {
      console.error(err);
      showSnackbar("Failed to update curriculum progress", "error");
    }
  };

  return (
    <div className="min-h-screen bg-[#F4F7FE]">

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3500}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Dialog
        open={deleteModalOpen}
        onClose={closeDeleteModal}
        aria-labelledby="delete-class-dialog-title"
      >
        <DialogTitle id="delete-class-dialog-title">Delete Group Class</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete
            {classToDelete?.title ? ` \"${classToDelete.title}\"` : " this class"}?
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteModal} color="inherit">
            Cancel
          </Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={createModalOpen}
        onClose={closeCreateModal}
        fullWidth
        maxWidth="md"
        aria-labelledby="create-class-dialog-title"
      >
        <DialogTitle id="create-class-dialog-title">Add New Group Class</DialogTitle>
        <form onSubmit={handleSubmit} className="space-y-6">
          <DialogContent dividers>
            <div>
              <label className="block mb-2 font-bold text-sm">Course Title</label>
              <input
                type="text"
                name="title"
                value={courseData.title}
                onChange={handleChange}
                className="w-full bg-slate-50 border p-4 rounded-xl"
                required
              />
            </div>

            <div className="grid md:grid-cols-2 gap-6 mt-4">
              <div>
                <label className="block mb-2 font-bold text-sm">Max Student</label>
                <input
                  type="number"
                  name="maxStudents"
                  value={courseData.maxStudents}
                  onChange={handleChange}
                  className="w-full bg-slate-50 border p-4 rounded-xl"
                />
              </div>

              <div>
                <label className="block mb-2 font-bold text-sm">Price</label>
                <input
                  type="number"
                  name="price"
                  value={courseData.price}
                  onChange={handleChange}
                  placeholder="Price"
                  className="w-full bg-slate-50 border p-4 rounded-xl"
                />
              </div>
            </div>

            <div className="mt-4">
              <label className="block mb-2 font-bold text-sm">Certificate Type</label>
              <select
                name="certificateType"
                value={courseData.certificateType}
                onChange={handleChange}
                className="w-full bg-slate-50 border p-4 rounded-xl"
              >
                <option value="global_certificate">Global Certificate</option>
                <option value="local_certificate_nigeria">Local Certificate (Nigeria)</option>
                <option value="none">None</option>
              </select>
            </div>

            <div className="mt-4">
              <div className="flex justify-between">
                <label className="font-bold text-sm">Description</label>
                <span className="text-xs">{wordCount} words</span>
              </div>

              <textarea
                name="description"
                rows={5}
                value={courseData.description}
                onChange={handleChange}
                className="w-full bg-slate-50 border p-4 rounded-xl"
              />
            </div>

            <div className="mt-4">
              <label className="block mb-2 font-bold text-sm">Class Link</label>
              <input
                type="url"
                name="classLink"
                value={courseData.classLink}
                onChange={handleChange}
                placeholder="Zoom / Google Meet / Classroom"
                className="w-full bg-slate-50 border p-4 rounded-xl"
              />
            </div>

            <div className="mt-4">
              <label className="block mb-2 font-bold text-sm">Video Link</label>
              <input
                type="url"
                name="videoLink"
                value={courseData.videoLink}
                onChange={handleChange}
                placeholder="YouTube link"
                className="w-full bg-slate-50 border p-4 rounded-xl"
              />
            </div>

            <div className="grid md:grid-cols-2 gap-6 mt-4">
              <div>
                <label className="block mb-2 font-bold text-sm">Start Date</label>
                <input
                  type="datetime-local"
                  name="startDate"
                  value={courseData.startDate}
                  onChange={handleChange}
                  className="w-full bg-slate-50 border p-4 rounded-xl"
                  required
                />
              </div>

              <div>
                <label className="block mb-2 font-bold text-sm">End Date</label>
                <input
                  type="datetime-local"
                  name="endDate"
                  value={courseData.endDate}
                  onChange={handleChange}
                  className="w-full bg-slate-50 border p-4 rounded-xl"
                  required
                />
              </div>
            </div>
          </DialogContent>

          <DialogActions>
            <Button onClick={closeCreateModal} color="inherit">Cancel</Button>
            <Button type="submit" variant="contained" disabled={isSubmitting}>
              {isSubmitting ? "Publishing..." : "Publish Class"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Dialog
        open={viewModalOpen}
        onClose={closeViewModal}
        fullWidth
        maxWidth="sm"
        aria-labelledby="view-class-dialog-title"
      >
        <DialogTitle id="view-class-dialog-title">Class Details</DialogTitle>
        <DialogContent dividers>
          {selectedCourse && (
            <div className="space-y-3 text-sm">
              <p><span className="font-semibold">Title:</span> {selectedCourse.title || "-"}</p>
              <p><span className="font-semibold">Price:</span> ₦{selectedCourse.price ?? "-"}</p>
              <p><span className="font-semibold">Max Students:</span> {selectedCourse.max_students ?? "-"}</p>
              <p><span className="font-semibold">Status:</span> {getStatus(selectedCourse)}</p>
              <p><span className="font-semibold">Start:</span> {formatDate(selectedCourse.start_date)}</p>
              <p><span className="font-semibold">End:</span> {formatDate(selectedCourse.end_date)}</p>
              <p><span className="font-semibold">Class Link:</span> {selectedCourse.class_link || "-"}</p>
              <p><span className="font-semibold">Video Link:</span> {selectedCourse.video_link || "-"}</p>
              {/* <p><span className="font-semibold">Curriculum:</span> {selectedCourse.curriculum || "-"}</p> */}
              <p>
                <span className="font-semibold">Certificate Type:</span>{" "}
                {selectedCourse.certificate_type === "global_certificate"
                  ? "Global Certificate"
                  : selectedCourse.certificate_type === "local_certificate_nigeria"
                  ? "Local Certificate (Nigeria)"
                  : "None"}
              </p>
              <div>
                <p className="font-semibold mb-1">Description:</p>
                <p className="text-slate-600 whitespace-pre-wrap">{selectedCourse.description || "-"}</p>
              </div>

              <div className="mt-4 border rounded-xl p-3 bg-slate-50">
                <div className="flex items-center justify-between mb-2">
                  <p className="font-semibold">Curriculum Progress</p>
                  <p className="text-xs font-bold text-slate-600">{curriculumSummary.progress || 0}%</p>
                </div>
                <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                  <div className="h-full bg-blue-600" style={{ width: `${curriculumSummary.progress || 0}%` }} />
                </div>

                <div className="mt-3 flex gap-2">
                  <input
                    type="text"
                    value={newCurriculumItem}
                    onChange={(e) => setNewCurriculumItem(e.target.value)}
                    placeholder="Add module/item"
                    className="w-full bg-white border p-2 rounded-lg text-sm"
                  />
                  <button
                    type="button"
                    onClick={addCurriculumItem}
                    className="px-3 py-2 rounded-lg bg-slate-900 text-white text-xs font-semibold"
                  >
                    Add
                  </button>
                </div>

                <div className="mt-3 space-y-2">
                  {curriculumLoading ? (
                    <p className="text-xs text-slate-500">Loading curriculum...</p>
                  ) : curriculumItems.length === 0 ? (
                    <p className="text-xs text-slate-500">No curriculum item added yet.</p>
                  ) : (
                    curriculumItems.map((item) => (
                      <label key={item.id} className="flex items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          checked={Number(item.is_completed) === 1}
                          onChange={() => toggleCurriculumItem(item)}
                        />
                        <span className={Number(item.is_completed) === 1 ? "line-through text-slate-400" : "text-slate-700"}>
                          {item.title}
                        </span>
                      </label>
                    ))
                  )}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeViewModal}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={editModalOpen}
        onClose={closeEditModal}
        fullWidth
        maxWidth="md"
        aria-labelledby="edit-class-dialog-title"
      >
        <DialogTitle id="edit-class-dialog-title">Update Class</DialogTitle>
        <form onSubmit={handleUpdate}>
          <DialogContent dividers>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block mb-1 text-sm font-bold">Course Title</label>
                <input
                  type="text"
                  name="title"
                  value={editData.title}
                  onChange={handleEditChange}
                  className="w-full bg-slate-50 border p-3 rounded-lg"
                  required
                />
              </div>

              <div>
                <label className="block mb-1 text-sm font-bold">Price</label>
                <input
                  type="number"
                  name="price"
                  value={editData.price}
                  onChange={handleEditChange}
                  className="w-full bg-slate-50 border p-3 rounded-lg"
                  required
                />
              </div>

              <div>
                <label className="block mb-1 text-sm font-bold">Max Students</label>
                <input
                  type="number"
                  name="maxStudents"
                  value={editData.maxStudents}
                  onChange={handleEditChange}
                  className="w-full bg-slate-50 border p-3 rounded-lg"
                />
              </div>

              <div>
                <label className="block mb-1 text-sm font-bold">End Date</label>
                <input
                  type="datetime-local"
                  name="endDate"
                  value={editData.endDate}
                  onChange={handleEditChange}
                  className="w-full bg-slate-50 border p-3 rounded-lg"
                  required
                />
              </div>
            </div>

            <div className="mt-4">
              <label className="block mb-1 text-sm font-bold">Class Link</label>
              <input
                type="url"
                name="classLink"
                value={editData.classLink}
                onChange={handleEditChange}
                className="w-full bg-slate-50 border p-3 rounded-lg"
                required
              />
            </div>

            <div className="mt-4">
              <label className="block mb-1 text-sm font-bold">Video Link</label>
              <input
                type="url"
                name="videoLink"
                value={editData.videoLink}
                onChange={handleEditChange}
                className="w-full bg-slate-50 border p-3 rounded-lg"
              />
            </div>

            <div className="mt-4">
              <label className="block mb-1 text-sm font-bold">Certificate Type</label>
              <select
                name="certificateType"
                value={editData.certificateType}
                onChange={handleEditChange}
                className="w-full bg-slate-50 border p-3 rounded-lg"
              >
                <option value="global_certificate">Global Certificate</option>
                <option value="local_certificate_nigeria">Local Certificate (Nigeria)</option>
                <option value="none">None</option>
              </select>
            </div>

            <div className="mt-4">
              <label className="block mb-1 text-sm font-bold">Description</label>
              <textarea
                rows={4}
                name="description"
                value={editData.description}
                onChange={handleEditChange}
                className="w-full bg-slate-50 border p-3 rounded-lg"
              />
            </div>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeEditModal} color="inherit">Cancel</Button>
            <Button type="submit" variant="contained" disabled={isUpdating}>
              {isUpdating ? "Updating..." : "Update Class"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <main className="max-w-5xl mx-auto px-4 py-10 space-y-14">

        {/* HEADER */}
        <header className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-4xl font-black">Group Class</h1>
            <p className="text-slate-500 mt-2">
              Create and manage group classes.
            </p>
          </div>
          <button
            type="button"
            onClick={openCreateModal}
            className="bg-slate-900 text-white px-5 py-3 rounded-xl text-sm font-semibold"
          >
            Add New Group Class
          </button>
        </header>

        <div className="inline-flex rounded-xl border border-slate-300 bg-white p-1">
          <button
            type="button"
            onClick={() => setActiveTab("classes")}
            className={`px-4 py-2 rounded-lg text-sm font-semibold ${
              activeTab === "classes" ? "bg-slate-900 text-white" : "text-slate-700"
            }`}
          >
            Created Classes
          </button>
          <button
            type="button"
            onClick={() => setActiveTab("students")}
            className={`px-4 py-2 rounded-lg text-sm font-semibold ${
              activeTab === "students" ? "bg-slate-900 text-white" : "text-slate-700"
            }`}
          >
            Manage Students
          </button>
        </div>

        {activeTab === "classes" && (
        <section className="space-y-6">
          <h2 className="text-2xl font-black">Your Classes</h2>

          {courses.length === 0 ? (
            <p>No classes yet</p>
          ) : (
            courses.map((course) => (
              <div key={course.id} className="bg-white p-6 rounded-2xl shadow">

                <div className="flex justify-between">
                  <h3 className="font-bold">{course.title}</h3>
                  <span>₦{course.price}</span>
                </div>

                <div className="mt-2">
                  <p className="text-xs text-slate-500 mb-1">Curriculum Progress: {Number(course.curriculum_progress || 0).toFixed(0)}%</p>
                  <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                    <div className="h-full bg-blue-600" style={{ width: `${Number(course.curriculum_progress || 0)}%` }} />
                  </div>
                </div>

                {/* DATES */}
                <p className="text-xs text-slate-500 mt-2">
                  Start: {formatDate(course.start_date)}
                </p>
                <p className="text-xs text-slate-500">
                  End: {formatDate(course.end_date)}
                </p>

                {/* STATUS */}
                <p className="text-xs font-bold mt-2 text-blue-600">
                  {getStatus(course)}
                </p>

                {/* COUPON */}
                <div className="mt-4 bg-slate-900 p-4 rounded-xl">
                  <p className="text-xs text-blue-400 mb-2">Coupon Code</p>

                  <button
                    type="button"
                    onClick={() => copyToClipboard(course.coupon_code)}
                    className="bg-white px-4 py-2 rounded text-xs font-mono flex gap-2 items-center"
                  >
                    {course.coupon_code}
                    <Copy size={12} />
                  </button>
                </div>

                <button
                  type="button"
                  onClick={() => copyShareLink(course.id)}
                  className="mt-3 text-xs font-semibold text-slate-700 underline"
                >
                  Copy share URL
                </button>

                <div className="mt-4 flex flex-wrap gap-3">
                  <button
                    type="button"
                    onClick={() => openViewModal(course)}
                    className="text-blue-600 text-sm flex items-center gap-2"
                  >
                    <Eye size={14} /> View
                  </button>

                  <button
                    type="button"
                    onClick={() => openEditModal(course)}
                    className="text-amber-600 text-sm flex items-center gap-2"
                  >
                    <FilePenLine size={14} /> Update
                  </button>

                  <button
                    type="button"
                    onClick={() => openDeleteModal(course)}
                    className="text-red-500 text-sm flex items-center gap-2"
                  >
                    <Trash2 size={14} /> Delete
                  </button>
                </div>

              </div>
            ))
          )}
        </section>
        )}

        {activeTab === "students" && (
          <GroupClassStudentsTab
            tutorId={tutor?.id}
            classes={courses}
            onReloadClasses={fetchCourses}
          />
        )}

      </main>
    </div>
  );
};

export default CreateCourseDashboard;